import { downloadNCRReport as apiDownloadNCRReport } from "../services/ncrService";

const sanitizePdfFileName = (text) =>
  String(text || "")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "") || "ncr_report";

/**
 * Loads and returns an NCR report blob directly from the backend for previewing.
 */
export async function loadNCRReportBlob(ncrOrId) {
  const id = typeof ncrOrId === "object" ? ncrOrId.id : ncrOrId;
  if (!id) throw new Error("NCR ID is required");

  const response = await apiDownloadNCRReport(id, { inline: 1 });
  const contentType = response?.headers?.["content-type"] || "application/pdf";
  const rawBlob =
    response?.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: contentType });

  if (contentType.includes("json") || rawBlob.type.includes("json")) {
    const errorText = await rawBlob.text();
    let detail = "Failed to load NCR report from backend";
    try {
      const parsed = JSON.parse(errorText);
      detail = parsed.detail || parsed.error || detail;
    } catch {}
    throw new Error(detail);
  }

  let filename = "";
  const contentDisposition = response?.headers?.["content-disposition"];
  if (contentDisposition) {
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    const normalMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    filename = utf8Match?.[1] ? decodeURIComponent(utf8Match[1]) : normalMatch?.[1] || "";
  }

  if (!filename) {
    const ncrNo = typeof ncrOrId === "object" ? ncrOrId.ncr_no : id;
    filename = `${sanitizePdfFileName(ncrNo || `ncr-${id}`)}_report.pdf`;
  }

  return { blob: new Blob([response.data], { type: "application/pdf" }), filename };
}

/**
 * Downloads the NCR report file directly from the backend.
 */
export async function downloadNCRReportFile(ncrOrId) {
  const id = typeof ncrOrId === "object" ? ncrOrId.id : ncrOrId;
  if (!id) throw new Error("NCR ID is required");

  const response = await apiDownloadNCRReport(id);
  const contentType = response?.headers?.["content-type"] || "application/pdf";
  const testBlob =
    response?.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: contentType });

  if (contentType.includes("json") || testBlob.type.includes("json")) {
    const errorText = await testBlob.text();
    let detail = "Failed to download NCR report from backend";
    try {
      const parsed = JSON.parse(errorText);
      detail = parsed.detail || parsed.error || detail;
    } catch {}
    throw new Error(detail);
  }

  let filename = "";
  const contentDisposition = response?.headers?.["content-disposition"];
  if (contentDisposition) {
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    const normalMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    filename = utf8Match?.[1] ? decodeURIComponent(utf8Match[1]) : normalMatch?.[1] || "";
  }

  if (!filename) {
    const ncrNo = typeof ncrOrId === "object" ? ncrOrId.ncr_no : id;
    filename = `${sanitizePdfFileName(ncrNo || `ncr-${id}`)}_report.pdf`;
  }

  const objectUrl = window.URL.createObjectURL(testBlob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => window.URL.revokeObjectURL(objectUrl), 3000);

  return { success: true, filename };
}
